/**
 * GSAP Player Controls Component
 * Modern, sleek player controls for GSAP slides using React
 * Communicates with iframe viewer via postMessage
 */

import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Play, Pause, RotateCcw, Maximize } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { SegmentedProgressBar, type ProgressSegment } from './SegmentedProgressBar';

export interface GsapPlayerControlsProps {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  isLoading?: boolean;
  onPlayPause: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onSeek: (time: number) => void;
  /**
   * Live drag handler. When provided, drag-move events go to onScrub instead of onSeek
   * (much cheaper — no slide fetches, no audio re-init). Pair with onScrubEnd.
   */
  onScrub?: (time: number) => void;
  /** Called when drag ends — should commit position and resume play if was playing. */
  onScrubEnd?: () => void;
  onRestart: () => void;
  onStepBackward: () => void;
  onStepForward: () => void;
  onSpeedChange?: (rate: number) => void;
  onFullscreen?: () => void;
  className?: string;
  /** Compact layout for narrow containers (<480px) */
  compact?: boolean;

  /**
   * Slide-granular timeline (segmented mode). When `segments` and `onSlideSelect`
   * are both provided, the continuous scrubber is replaced by a per-slide segmented
   * bar: clicking a segment jumps to that slide's start, and there is no
   * drag-to-arbitrary-time within a slide. The drag/scrub handlers above are ignored
   * in this mode. Pair with currentSlideIndex/currentSlideTime/currentSlideDuration
   * so the active segment fills exactly with playback.
   */
  segments?: ProgressSegment[];
  onSlideSelect?: (index: number) => void;
  currentSlideIndex?: number;
  currentSlideTime?: number;
  currentSlideDuration?: number;
}

export const GsapPlayerControls: React.FC<GsapPlayerControlsProps> = ({
  isPlaying,
  currentTime,
  duration,
  playbackRate,
  isLoading = false,
  onPlayPause,
  onPlay,
  onPause,
  onSeek,
  onScrub,
  onScrubEnd,
  onRestart,
  onSpeedChange,
  onFullscreen,
  className = '',
  compact = false,
  segments,
  onSlideSelect,
  currentSlideIndex = 0,
  currentSlideTime = 0,
  currentSlideDuration = 0,
}) => {
  // Segmented mode replaces the continuous scrubber with slide-granular navigation.
  const segmentedMode = !!segments && !!onSlideSelect;
  // Use scrub-path during drag when available; fall back to onSeek for backward compatibility.
  const liveSeek = onScrub ?? onSeek;
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const wasPlayingRef = useRef(false);

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle timeline hover for preview
  const handleTimelineHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || isDragging) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = Math.max(0, Math.min(duration, percent * duration));

    setHoverTime(time);
    setHoverPosition(e.clientX - rect.left);
  };

  const handleTimelineLeave = () => {
    if (!isDragging) {
      setHoverTime(null);
    }
  };

  // Handle scrubbing (mouse)
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || isLoading || !duration) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const time = percent * duration;

    setIsDragging(true);
    wasPlayingRef.current = isPlaying;

    // NOTE: when scrub() path is active we deliberately do NOT call onPause here — the controller's
    // scrub() pauses the timeline implicitly and tracks _scrubStartPlaying so scrubEnd can resume.
    // Calling onPause would clear that flag and prevent auto-resume on release.
    if (isPlaying && !onScrub) {
      if (onPause) {
        onPause();
      } else {
        onPlayPause();
      }
    }

    // Perform initial seek (cheap during drag)
    liveSeek(time);
  };

  // Handle scrubbing (touch)
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!progressRef.current || isLoading || !duration) return;
    e.stopPropagation();

    const touch = e.touches[0];
    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    const time = percent * duration;

    setIsDragging(true);
    wasPlayingRef.current = isPlaying;

    if (isPlaying && !onScrub) {
      if (onPause) {
        onPause();
      } else {
        onPlayPause();
      }
    }

    liveSeek(time);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!progressRef.current) return;

      const rect = progressRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const time = percent * duration;

      liveSeek(time);
      setHoverTime(time);
      setHoverPosition(Math.max(0, Math.min(rect.width, e.clientX - rect.left)));
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!progressRef.current) return;
      e.preventDefault(); // Prevent scroll while scrubbing

      const touch = e.touches[0];
      const rect = progressRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
      const time = percent * duration;

      liveSeek(time);
      setHoverTime(time);
      setHoverPosition(Math.max(0, Math.min(rect.width, touch.clientX - rect.left)));
    };

    const handleEnd = () => {
      setIsDragging(false);
      setHoverTime(null);

      if (onScrubEnd) {
        // Scrub path: scrubEnd commits the position and auto-resumes if was playing.
        onScrubEnd();
      } else {
        // Legacy path (no scrub wired): manually resume playback if was playing.
        if (wasPlayingRef.current && !isPlaying) {
          if (onPlay) {
            onPlay();
          } else {
            onPlayPause();
          }
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, duration, liveSeek, onScrubEnd, onPlay, isPlaying, onPlayPause]);

  return (
    <div className={cn(
      "bg-black/60 backdrop-blur-xl border border-white/10 rounded-full shadow-2xl",
      "transition-all duration-300 hover:bg-black/70",
      compact ? "px-3 py-1.5" : "px-4 py-2 mx-auto max-w-2xl",
      className
    )}>
      {/* Single Row Layout - All controls in one line */}
      <div className={cn("flex items-center", compact ? "gap-2" : "gap-4")}>
        {/* Play/Pause Button - Prominent */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onPlayPause}
          disabled={isLoading}
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all hover:scale-105 flex-shrink-0"
          title={isPlaying ? "Pause (Space)" : "Play (Space)"}
        >
          {isPlaying ? (
            <Pause className="h-5 w-5 fill-current" />
          ) : (
            <Play className="h-5 w-5 ml-0.5 fill-current" />
          )}
        </Button>

        {/* Current Time - hidden in compact mode */}
        {!compact && (
          <span className="text-white/80 text-xs font-medium font-mono w-10 text-right flex-shrink-0">
            {formatTime(currentTime)}
          </span>
        )}

        {/* Segmented Timeline (slide-granular) — replaces the continuous scrubber.
            Click a segment to jump to that slide's start; no drag-to-scrub. */}
        {segmentedMode ? (
          <div className="relative flex-1 flex items-center">
            <SegmentedProgressBar
              segments={segments!}
              currentSlideIndex={currentSlideIndex}
              currentSlideTime={currentSlideTime}
              currentSlideDuration={currentSlideDuration}
              onSelect={onSlideSelect!}
              disabled={isLoading}
              compact={compact}
            />
          </div>
        ) : (
        /* Timeline Progress Bar */
        <div
          ref={progressRef}
          className="relative flex-1 h-8 flex items-center cursor-pointer group"
          onMouseMove={handleTimelineHover}
          onMouseLeave={handleTimelineLeave}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Background Track */}
          <div className="absolute inset-x-0 h-1.5 bg-white/10 rounded-full overflow-hidden group-hover:h-2 transition-all duration-200">
            {/* Filled Progress */}
            <div
              className="absolute left-0 h-full rounded-full transition-none bg-primary"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            />
          </div>

          {/* Progress Handle - Only visible on hover/drag */}
          <div
            className={cn(
              "absolute top-1/2 w-3.5 h-3.5 bg-white rounded-full transform -translate-y-1/2 -translate-x-1/2 z-10",
              "transition-all duration-200 shadow-[0_0_10px_rgba(255,255,255,0.5)]",
              "opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100",
              isDragging && "scale-125 opacity-100 ring-4 ring-white/20"
            )}
            style={{ left: `${(currentTime / duration) * 100}%` }}
          />

          {/* Hover Time Tooltip */}
          {hoverTime !== null && !isLoading && (
            <div
              className="absolute -top-10 bg-black/80 text-white text-xs px-2 py-1 rounded-md border border-white/10 shadow-xl pointer-events-none z-50 backdrop-blur-sm"
              style={{
                left: hoverPosition,
                transform: 'translateX(-50%)'
              }}
            >
              {formatTime(hoverTime)}
            </div>
          )}

          {/* Buffering Indicator */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-full bg-white/5 animate-pulse rounded-full" />
            </div>
          )}
        </div>
        )}

        {/* Duration - hidden in compact mode */}
        {!compact && (
          <span className="text-white/50 text-xs font-medium font-mono w-10 flex-shrink-0">
            {formatTime(duration)}
          </span>
        )}

        {/* Divider + Secondary Controls - hidden in compact mode */}
        {!compact && (
          <>
            <div className="w-px h-4 bg-white/10 flex-shrink-0" />

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Restart Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onRestart}
                disabled={isLoading}
                className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all"
                title="Restart (R)"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>

              {/* Speed Control */}
              {onSpeedChange && (
                <Select
                  value={playbackRate.toString()}
                  onValueChange={(value) => onSpeedChange(parseFloat(value))}
                  disabled={isLoading}
                >
                  <SelectTrigger className="w-14 h-8 text-xs bg-transparent border-0 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors focus:ring-0">
                    <span className="font-medium">{playbackRate}x</span>
                  </SelectTrigger>
                  <SelectContent className="bg-black/90 backdrop-blur-xl border-white/10 text-white min-w-[80px]">
                    <SelectItem value="0.5" className="text-xs focus:bg-white/20 focus:text-white">0.5×</SelectItem>
                    <SelectItem value="1" className="text-xs focus:bg-white/20 focus:text-white">1×</SelectItem>
                    <SelectItem value="1.5" className="text-xs focus:bg-white/20 focus:text-white">1.5×</SelectItem>
                    <SelectItem value="2" className="text-xs focus:bg-white/20 focus:text-white">2×</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Fullscreen */}
              {onFullscreen && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onFullscreen}
                  disabled={isLoading}
                  className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all"
                  title="Fullscreen (F)"
                >
                  <Maximize className="h-4 w-4" />
                </Button>
              )}
            </div>
          </>
        )}

        {/* Fullscreen - shown standalone in compact mode */}
        {compact && onFullscreen && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onFullscreen}
            disabled={isLoading}
            className="h-10 w-10 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all flex-shrink-0"
            title="Fullscreen (F)"
          >
            <Maximize className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * Auto-hide wrapper for player controls
 */
export interface AutoHidePlayerControlsRef {
  show: () => void;
}

export const AutoHidePlayerControls = forwardRef<AutoHidePlayerControlsRef, {
  children: React.ReactNode;
  isPlaying: boolean;
  isFullscreen?: boolean;
  className?: string;
  /**
   * When true, controls are pinned visible and never auto-hide — used for the public player's
   * sticky control bar (discoverability: there is no gesture to learn). Applies inline AND in
   * fullscreen. Defaults to false (legacy auto-hide behavior).
   */
  alwaysVisible?: boolean;
}>(({ children, isPlaying, isFullscreen = false, className, alwaysVisible = false }, ref) => {
  const [visible, setVisible] = useState(true);
  const hideTimer = useRef<NodeJS.Timeout | undefined>(undefined);

  const showControls = () => {
    setVisible(true);
    clearTimeout(hideTimer.current);

    // Auto-hide after 3 seconds if playing — skipped entirely when pinned.
    if (isPlaying && !alwaysVisible) {
      hideTimer.current = setTimeout(() => {
        setVisible(false);
      }, 3000);
    }
  };

  // Expose show() so parent can trigger via a bottom-edge hover zone
  useImperativeHandle(ref, () => ({ show: showControls }));

  useEffect(() => {
    showControls();
  }, [isPlaying, alwaysVisible]);

  // Reset visibility when fullscreen changes
  useEffect(() => {
    showControls();
  }, [isFullscreen]);

  useEffect(() => {
    return () => {
      clearTimeout(hideTimer.current);
    };
  }, []);

  const effectiveVisible = alwaysVisible || visible;

  return (
    <div
      onMouseMove={showControls}
      onTouchStart={showControls}
      onMouseLeave={() => !alwaysVisible && isPlaying && setVisible(false)}
      className={className}
      // When hidden, let pointer events pass through to iframe (e.g. Continue button)
      // When visible, capture events normally for controls interaction
      style={{ pointerEvents: effectiveVisible ? 'auto' : 'none' }}
    >
      <div
        className={cn(
          "transition-all duration-500 ease-out transform",
          effectiveVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        )}
      >
        {children}
      </div>
    </div>
  );
});